import React, { useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { ExclamationTriangleIcon, InformationCircleIcon, PencilSquareIcon, PrinterIcon } from "@heroicons/react/24/outline";
import CustomDialog from "../modals/CustomDialog/CustomDialog";
import { Button } from "../forms/Buttons/Button";
import { isBlankHtml, RichText } from "../QuestionFolders/ImportPreviewQuestion";
import { getApiErrorCode } from "../../utils/apiError";
import { UserContext } from "../../utils/contexts/UserContext";
import { canOpenPage } from "../../utils/permissions";
import { focusRing, linkButtonClass } from "../../pages/Exams/TestBuilder/ui";
import QuestionContent from "./QuestionContent";
import { BookletChips, dialogFooterClass, fieldClass } from "./variantUi";
import { variantErrorMessage } from "./variantText";

const quillClass =
  "rounded-lg bg-white shadow-sm [&_.ql-container]:!border-slate-300 [&_.ql-container]:rounded-b-lg [&_.ql-editor]:min-h-[7rem] [&_.ql-editor]:text-sm [&_.ql-toolbar]:!border-slate-300 [&_.ql-toolbar]:rounded-t-lg [&_.ql-toolbar]:bg-slate-50";
const checkboxClass = "h-4 w-4 rounded border-slate-300 text-blue-600 transition focus:ring-2 focus:ring-blue-600/30 focus:ring-offset-0";
const alertClass = "flex items-start gap-2 rounded-xl border px-3 py-2 text-sm";
// Quill 1 has no tables: any edit of information with a docx table saves it flattened to plain lines.
const hasTables = (html) => /<table[\s>]/i.test(html || "");

// What a booklet prints of a variant besides its questions: a change of it takes the confirmation off.
const PRINTED = ["heading", "calculator_is_allowed", "periodic_table_is_allowed", "additional_information"];
const TEXTS = ["name", "heading", "description"];
const FLAGS = ["calculator_is_allowed", "periodic_table_is_allowed"];
const FIELDS = [...TEXTS, ...FLAGS, "additional_information"];
const formOf = (variant) => ({
  name: variant?.name || "",
  heading: variant?.heading || "",
  description: variant?.description || "",
  calculator_is_allowed: !!variant?.calculator_is_allowed,
  periodic_table_is_allowed: !!variant?.periodic_table_is_allowed,
  additional_information: variant?.additional_information || "",
});
// Quill's empty "<p><br></p>" is no information.
const info = (html) => (isBlankHtml(html) ? "" : html);
// Two values of a field are the same as saved: texts trimmed, the information blank or as it is.
const sameField = (key, a, b) => {
  if (FLAGS.includes(key)) return a === b;
  if (key === "additional_information") return info(a) === info(b);
  return (a || "").trim() === (b || "").trim();
};

// The dialog's edit: the form, the variant's values it started from (`base`, of `revision`), and what someone
// else changed meanwhile (`notes`: {field: {kind: "updated"} | {kind: "conflict", value}}).
const startEdit = (variant) => ({ form: formOf(variant), base: formOf(variant), revision: variant?.revision, notes: {}, infoEdited: false });

/**
 * The variant read again while the dialog is open (after a 409 variant_changed, or a reload when the tab comes back):
 * a field the user has not changed takes the new value; one both changed keeps the user's and says what the other
 * one is. The form's base moves to the new variant, so only the user's own changes are ever sent.
 */
function rebase(edit, variant) {
  if (!variant || String(variant.revision) === String(edit.revision)) return edit;
  const fresh = formOf(variant);
  const form = { ...edit.form };
  const notes = { ...edit.notes };
  FIELDS.forEach((key) => {
    if (sameField(key, fresh[key], edit.base[key])) return;
    const edited = !sameField(key, edit.form[key], edit.base[key]) && (key !== "additional_information" || edit.infoEdited);
    if (!edited) {
      form[key] = fresh[key];
      notes[key] = { kind: "updated" };
    } else if (sameField(key, edit.form[key], fresh[key])) {
      delete notes[key];
    } else {
      notes[key] = { kind: "conflict", value: fresh[key] };
    }
  });
  return { ...edit, form, base: fresh, revision: variant.revision, notes };
}

// "Printed" next to a field a booklet prints.
function PrintedMark() {
  const [t] = useTranslation();
  return (
    <span className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 align-middle text-[11px] font-medium text-slate-600">
      <PrinterIcon className="h-3 w-3" aria-hidden="true" />
      {t("variants.settings.printed")}
    </span>
  );
}

// After a refused save, once its errors are on screen: the focus on the first field they name, else on the form's
// message. `ask()` asks for it with the errors' state update.
export function useErrorFocus(formRef) {
  const [asked, setAsked] = useState(0);
  useEffect(() => {
    if (!asked) return;
    const form = formRef.current;
    (form?.querySelector('[data-invalid="true"], [aria-invalid="true"]') || form?.querySelector("[data-form-alert]"))?.focus();
  }, [asked, formRef]);
  return () => setAsked((n) => n + 1);
}

/**
 * The variant's settings: name and description (only here), and what a booklet prints of it (heading, calculator,
 * periodic table, additional information). Only the fields the user changed are sent: a change of a printed one
 * takes the confirmation off, which the dialog says before saving. `onSave(fields, revision)` writes them, with the
 * revision of the variant the form was based on, and rejects with the server's error. When the variant is read
 * again while the dialog is open (a 409 variant_changed reads it again), the form moves on to it (rebase).
 */
export function SettingsDialog({ isOpen, variant, onClose, onSave }) {
  const [t] = useTranslation();
  const id = useId();
  const formRef = useRef(null);
  const [edit, setEdit] = useState(() => startEdit(variant));
  const [tablesUnlocked, setTablesUnlocked] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const focusError = useErrorFocus(formRef);
  const { form, base, notes, infoEdited } = edit;

  // Opened: the variant as it is. Open and read again: rebased on it.
  const wasOpen = useRef(false);
  const revision = variant?.revision;
  useEffect(() => {
    if (!isOpen) {
      wasOpen.current = false;
      return;
    }
    if (wasOpen.current) {
      setEdit((prev) => rebase(prev, variant));
      return;
    }
    wasOpen.current = true;
    setEdit(startEdit(variant));
    setTablesUnlocked(false);
    setErrors({});
  }, [isOpen, revision]); // eslint-disable-line react-hooks/exhaustive-deps

  const changes = useMemo(() => {
    const fields = {};
    TEXTS.forEach((key) => {
      if (!sameField(key, form[key], base[key])) fields[key] = form[key].trim();
    });
    FLAGS.forEach((key) => {
      if (form[key] !== base[key]) fields[key] = form[key];
    });
    // Quill rewrites HTML it did not produce: the information only counts as changed once the user edits it.
    if (infoEdited && !sameField("additional_information", form.additional_information, base.additional_information)) {
      fields.additional_information = info(form.additional_information);
    }
    return fields;
  }, [form, base, infoEdited]);
  const dirty = Object.keys(changes).length > 0;
  const printedChanged = PRINTED.some((key) => key in changes);
  const storedHasTables = useMemo(() => hasTables(base.additional_information), [base.additional_information]);
  const infoLocked = storedHasTables && !tablesUnlocked;

  const update = (key, value) => {
    setEdit((prev) => ({ ...prev, form: { ...prev.form, [key]: value } }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };
  // The other person's value of a field both changed.
  const takeTheirs = (key) =>
    setEdit((prev) => {
      const notesLeft = { ...prev.notes };
      delete notesLeft[key];
      return {
        ...prev,
        form: { ...prev.form, [key]: prev.base[key] },
        notes: notesLeft,
        infoEdited: key === "additional_information" ? false : prev.infoEdited,
      };
    });
  const close = () => {
    if (saving) return;
    if (dirty && !window.confirm(t("variants.settings.discard_confirm"))) return;
    onClose();
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setErrors({ name: t("variants.settings.name_required") });
      focusError();
      return;
    }
    if (!dirty) {
      onClose();
      return;
    }
    setSaving(true);
    setErrors({});
    try {
      await onSave(changes, edit.revision);
      toast.success(t("variants.toast.settings_saved"));
      onClose();
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 400 && data && typeof data === "object" && !data.code) {
        const field = (key) => [].concat(data[key] || []).join(" ");
        setErrors({
          name: field("name"),
          heading: field("heading"),
          description: field("description"),
          additional_information: field("additional_information"),
          form: field("detail") || field("non_field_errors"),
        });
      } else {
        const code = getApiErrorCode(err);
        setErrors({
          form: code === "variant_changed" ? t("variants.settings.changed_meanwhile") : variantErrorMessage(t, err, { fallbackKey: "variants.settings.failed" }),
        });
      }
      focusError();
    } finally {
      setSaving(false);
    }
  };

  const fieldError = (key) =>
    errors[key] ? (
      <p id={`${id}-${key}-error`} role="alert" className="mt-1.5 text-sm text-red-600">
        {errors[key]}
      </p>
    ) : null;
  // What someone else changed in a field meanwhile: the new value shown, or both changed it (the user's stays).
  const meanwhile = (key) => {
    const note = notes[key];
    if (!note) return null;
    if (note.kind === "updated") {
      return (
        <p id={`${id}-${key}-note`} className="mt-1.5 flex items-start gap-1.5 text-xs text-blue-800">
          <InformationCircleIcon className="h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />
          {t("variants.settings.updated_meanwhile")}
        </p>
      );
    }
    if (sameField(key, form[key], note.value)) return null;
    const text =
      key === "additional_information"
        ? t("variants.settings.conflict_info")
        : (note.value || "").trim()
          ? t("variants.settings.conflict", { value: note.value.trim() })
          : t("variants.settings.conflict_empty");
    return (
      <div id={`${id}-${key}-note`} className="mt-1.5 flex flex-wrap items-start gap-x-2 gap-y-1 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900">
        <ExclamationTriangleIcon className="mt-px h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
        <span className="min-w-0 flex-1 whitespace-pre-line [overflow-wrap:anywhere]">{text}</span>
        <button type="button" onClick={() => takeTheirs(key)} className={`${linkButtonClass} -my-1 py-0.5 text-xs max-sm:min-h-11`}>
          {t("variants.settings.take_theirs")}
        </button>
      </div>
    );
  };
  const describedBy = (key, ...more) =>
    [...more, errors[key] ? `${id}-${key}-error` : null, notes[key] ? `${id}-${key}-note` : null].filter(Boolean).join(" ") || undefined;

  // Quill's editing area is a contenteditable with no name: the caption above it names it.
  const labelEditor = (quill) => {
    const root = quill?.editor?.root;
    if (!root) return;
    root.setAttribute("role", "textbox");
    root.setAttribute("aria-multiline", "true");
    root.setAttribute("aria-labelledby", `${id}-info-label`);
    const described = describedBy("additional_information");
    if (described) root.setAttribute("aria-describedby", described);
    else root.removeAttribute("aria-describedby");
    if (errors.additional_information) root.setAttribute("aria-invalid", "true");
    else root.removeAttribute("aria-invalid");
  };

  return (
    <CustomDialog isOpen={isOpen} closeModal={close} headerText={t("variants.settings.title")} maxWidth="max-w-3xl">
      <form ref={formRef} onSubmit={save} noValidate className="mt-4 space-y-5">
        <div>
          <label htmlFor={`${id}-name`} className="mb-1.5 block text-sm font-medium text-slate-700">
            {t("variants.fields.name")}
          </label>
          <input
            id={`${id}-name`}
            type="text"
            value={form.name}
            maxLength={255}
            onChange={(e) => update("name", e.target.value)}
            aria-invalid={!!errors.name}
            aria-describedby={describedBy("name")}
            className={`${fieldClass} h-10 ${errors.name ? "border-red-300" : ""}`}
          />
          {fieldError("name")}
          {meanwhile("name")}
        </div>
        <div>
          <label htmlFor={`${id}-heading`} className="mb-1.5 block text-sm font-medium text-slate-700">
            {t("variants.fields.heading")}
            <PrintedMark />
          </label>
          <input
            id={`${id}-heading`}
            type="text"
            value={form.heading}
            maxLength={120}
            onChange={(e) => update("heading", e.target.value)}
            aria-invalid={!!errors.heading}
            aria-describedby={[`${id}-heading-hint`, notes.heading ? `${id}-heading-note` : null].filter(Boolean).join(" ")}
            className={`${fieldClass} h-10 ${errors.heading ? "border-red-300" : ""}`}
          />
          <p id={`${id}-heading-hint`} className="mt-1.5 text-xs text-slate-500">
            {errors.heading ? <span className="text-red-600">{errors.heading}</span> : t("variants.fields.heading_hint")}
          </p>
          {meanwhile("heading")}
        </div>
        <div>
          <label htmlFor={`${id}-description`} className="mb-1.5 block text-sm font-medium text-slate-700">
            {t("variants.fields.description")} <span className="font-normal text-slate-500">({t("test_bank.form.optional")})</span>
          </label>
          <textarea
            id={`${id}-description`}
            rows={2}
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            aria-invalid={!!errors.description}
            aria-describedby={describedBy("description")}
            className={`${fieldClass} resize-y py-2 ${errors.description ? "border-red-300" : ""}`}
          />
          {fieldError("description")}
          {meanwhile("description")}
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          {FLAGS.map((key) => (
            <div key={key}>
              <label className="flex cursor-pointer select-none items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 transition duration-200 hover:border-slate-300">
                <input
                  type="checkbox"
                  checked={form[key]}
                  onChange={(e) => update(key, e.target.checked)}
                  aria-describedby={describedBy(key)}
                  className={checkboxClass}
                />
                <span className="text-sm font-medium text-slate-700">
                  {t(key === "calculator_is_allowed" ? "test_bank.form.calculator_allowed" : "test_bank.form.periodic_table_allowed")}
                  <PrintedMark />
                </span>
              </label>
              {meanwhile(key)}
            </div>
          ))}
        </div>
        <div>
          <p id={`${id}-info-label`} className="mb-2 text-sm font-medium text-slate-700">
            {t("test_bank.form.additional_information")}
            <PrintedMark />
          </p>
          {infoLocked ? (
            <div className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-amber-900">{t("test_bank.editor.tables_locked")}</p>
              <button
                type="button"
                onClick={() => window.confirm(t("test_bank.editor.tables_edit_confirm")) && setTablesUnlocked(true)}
                className={`inline-flex h-8 shrink-0 items-center gap-1.5 self-start rounded-lg bg-white px-3 text-xs font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 sm:self-auto ${focusRing}`}
              >
                <PencilSquareIcon className="h-4 w-4 text-slate-500" aria-hidden="true" />
                {t("test_bank.editor.tables_edit_anyway")}
              </button>
            </div>
          ) : (
            // tabIndex 0: Headless UI's first focus goes by tabindex, and Quill's -1 would put it here, not on the name.
            <ReactQuill
              ref={labelEditor}
              theme="snow"
              tabIndex={0}
              value={form.additional_information}
              onChange={(value, _delta, source) =>
                setEdit((prev) => ({
                  ...prev,
                  form: { ...prev.form, additional_information: value },
                  infoEdited: prev.infoEdited || source === "user",
                }))
              }
              className={quillClass}
            />
          )}
          {infoLocked && !isBlankHtml(base.additional_information) && (
            <div className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              <RichText html={base.additional_information} />
            </div>
          )}
          {fieldError("additional_information")}
          {meanwhile("additional_information")}
        </div>

        {variant?.status === "confirmed" && printedChanged ? (
          <p role="status" className={`${alertClass} border-amber-200 bg-amber-50 text-amber-900`}>
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
            {t("variants.settings.unconfirm_warning")}
          </p>
        ) : (
          <p className="text-xs text-slate-500">{t("variants.settings.printed_hint")}</p>
        )}
        {errors.form && (
          <p role="alert" tabIndex={-1} data-form-alert className={`${alertClass} border-red-200 bg-red-50 text-red-700 outline-none`}>
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            {errors.form}
          </p>
        )}
        <div className={dialogFooterClass}>
          <Button type="button" text={t("buttons.cancel")} color="secondary-outline" onClick={close} disabled={saving} />
          <Button type="submit" text={t("buttons.save")} color="primary" loading={saving} disabled={saving} />
        </div>
      </form>
    </CustomDialog>
  );
}

/**
 * Asks before a write that cannot be undone from here (propose again, delete, remove a passage). `children` say
 * more; `danger` paints the confirm button red; `disabled` keeps it off (the dialog says why). While the dialog
 * fades out its confirm button is off too, so a double click confirms once.
 */
export function ConfirmDialog({ isOpen, title, text, children, confirmLabel, danger = false, disabled = false, busy = false, onClose, onConfirm }) {
  const [t] = useTranslation();
  return (
    <CustomDialog isOpen={isOpen} closeModal={busy ? () => {} : onClose} headerText={title} maxWidth="max-w-lg">
      {text && <p className="mt-2 text-sm text-slate-600">{text}</p>}
      {children}
      <div className={dialogFooterClass}>
        <Button type="button" text={t("buttons.cancel")} color="secondary-outline" onClick={onClose} disabled={busy} />
        <Button
          type="button"
          text={confirmLabel}
          color={danger ? "error-solid" : "primary"}
          onClick={onConfirm}
          loading={busy}
          disabled={!isOpen || disabled || busy}
        />
      </div>
    </CustomDialog>
  );
}

// "Back to draft", with an optional reason that goes into the variant's log.
export function UnconfirmDialog({ isOpen, onClose, onConfirm }) {
  const [t] = useTranslation();
  const id = useId();
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (isOpen) setReason("");
  }, [isOpen]);
  return (
    <ConfirmDialog
      isOpen={isOpen}
      title={t("variants.unconfirm.title")}
      text={t("variants.unconfirm.text")}
      confirmLabel={t("variants.actions.unconfirm")}
      onClose={onClose}
      onConfirm={() => onConfirm(reason.trim())}
    >
      <div className="mt-4">
        <label htmlFor={`${id}-reason`} className="mb-1.5 block text-sm font-medium text-slate-700">
          {t("variants.unconfirm.reason")} <span className="font-normal text-slate-500">({t("test_bank.form.optional")})</span>
        </label>
        <textarea
          id={`${id}-reason`}
          rows={3}
          maxLength={500}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t("variants.unconfirm.reason_placeholder")}
          className={`${fieldClass} resize-y py-2`}
        />
      </div>
    </ConfirmDialog>
  );
}

/**
 * Delete. A variant a booklet holds can't go (409 variant_in_booklet): the dialog only says why, and to an admin
 * how (take it out of the booklet, an archived one back to draft first) with links to the booklets.
 */
export function DeleteDialog({ isOpen, variant, busy, onClose, onConfirm }) {
  const [t] = useTranslation();
  const { user } = useContext(UserContext) || {};
  const booklets = variant?.booklets || [];
  if (booklets.length) {
    const admin = canOpenPage(user, "/bts");
    return (
      <CustomDialog isOpen={isOpen} closeModal={onClose} headerText={t("variants.delete.blocked_title")} maxWidth="max-w-lg">
        <div role="note" className={`${alertClass} mt-3 flex-col border-amber-200 bg-amber-50 text-amber-900`}>
          <p>{admin ? t("variants.delete.in_booklet_admin") : t("variants.delete.in_booklet")}</p>
          {admin && booklets.some((booklet) => booklet.status === "archived") && <p>{t("variants.delete.archived_note")}</p>}
          <BookletChips booklets={booklets} linked={admin} />
        </div>
        <div className={dialogFooterClass}>
          <Button type="button" text={t("buttons.close")} color="secondary-outline" onClick={onClose} />
        </div>
      </CustomDialog>
    );
  }
  return (
    <ConfirmDialog
      isOpen={isOpen}
      title={t("variants.delete.title")}
      text={t("variants.delete.text", { name: variant?.name || "" })}
      confirmLabel={t("buttons.delete")}
      danger
      busy={busy}
      onClose={onClose}
      onConfirm={onConfirm}
    />
  );
}

// A question of the variant, read only: its body, options and passage.
export function QuestionDialog({ isOpen, tq, onClose }) {
  const [t] = useTranslation();
  return (
    <CustomDialog isOpen={isOpen} closeModal={onClose} headerText={t("variants.question_title", { number: tq?.order ?? "" })} maxWidth="max-w-3xl">
      <div className="mt-3">{tq && <QuestionContent question={tq.question} number={tq.order} />}</div>
      <div className={dialogFooterClass}>
        <Button type="button" text={t("buttons.close")} color="secondary-outline" onClick={onClose} />
      </div>
    </CustomDialog>
  );
}
